import { Platform } from 'react-native';

// Hard safety net for how long we'll wait for LTO to paint before falling
// back to evergreen. This is *not* the tuning knob it used to be — the disk
// cache made cache-hit paint effectively instant (<200ms), so this cap now
// only matters on cold installs (or the first launch after a new LTO ships).
// Comfortably clears wifi first-installs; slower networks fall back to
// evergreen on that first launch and get LTO on the next one once the cache
// is seeded.
//
// Both `waitForHeroSplashReady` and the widget's own fallback timer read this
// constant so splash and widget always agree on when to give up.
export const HERO_LTO_SPLASH_CAP_MS = 2000;

// Backstop for the evergreen fallback: the evergreen is a bundled asset and
// paints fast, but this guarantees the splash can never hang on a stuck decoder.
export const EVERGREEN_SAFETY_CAP_MS = 800;

// A paint-truth signal for the hero video. `readyToPlay` is not enough — on iOS
// a cached source can reach `readyToPlay` while the surface stays black (see
// expo/expo#43957). So the on-screen layer reports when it has actually painted
// its first frame (fade-in), and the splash waits on that rather than on preload
// readiness.

type Waiter = { predicate: () => boolean; resolve: () => void };

// The key of the layer that has painted a real frame, if any.
let paintedKey: string | null = null;
// Keys the on-screen stack has reported as errored.
const erroredKeys = new Set<string>();
// Keys that deferred to the bundled evergreen — errored, or too slow
const fallenBackKeys = new Set<string>();
let fallbackListeners: (() => void)[] = [];
let waiters: Waiter[] = [];

function flush(): void {
  if (waiters.length === 0) {
    return;
  }
  const remaining: Waiter[] = [];
  for (const waiter of waiters) {
    if (waiter.predicate()) {
      waiter.resolve();
    } else {
      remaining.push(waiter);
    }
  }
  waiters = remaining;
}

export function reportHeroFramePainted(key: string): void {
  paintedKey = key;
  flush();
}

export function reportHeroVideoErrored(key: string): void {
  erroredKeys.add(key);
  flush();
}

export function isHeroFramePainted(key: string): boolean {
  return paintedKey === key;
}

export function isHeroVideoErrored(key: string): boolean {
  return erroredKeys.has(key);
}

function notifyFallbackListeners(): void {
  for (const listener of fallbackListeners) {
    listener();
  }
}

export function reportHeroVideoFellBack(key: string): void {
  if (fallenBackKeys.has(key)) {
    return;
  }
  fallenBackKeys.add(key);
  notifyFallbackListeners();
}

export function isHeroVideoFallenBack(key: string): boolean {
  return fallenBackKeys.has(key);
}

export function subscribeHeroFallback(listener: () => void): () => void {
  fallbackListeners.push(listener);
  return () => {
    fallbackListeners = fallbackListeners.filter(existing => existing !== listener);
  };
}

// Clears just the fallback set, letting the greeting swap back to the LTO
export function resetHeroFallback(): void {
  if (fallenBackKeys.size === 0) {
    return;
  }
  fallenBackKeys.clear();
  notifyFallbackListeners();
}

// Clears state so a recreated video generation (foreground resume) starts fresh.
export function resetHeroReadySignal(): void {
  paintedKey = null;
  erroredKeys.clear();
  resetHeroFallback();
}

// Resolves once `predicate` is satisfied by a paint/error report, or after
// `timeoutMs`, whichever comes first. Resolves immediately on web (no native
// video) or when the predicate already holds.
export function waitForHeroSignal(predicate: () => boolean, timeoutMs?: number): Promise<void> {
  if (Platform.OS === 'web') {
    return Promise.resolve();
  }
  if (predicate()) {
    return Promise.resolve();
  }
  return new Promise<void>(resolve => {
    let settled = false;
    const done = () => {
      if (settled) {
        return;
      }
      settled = true;
      resolve();
    };
    waiters.push({ predicate, resolve: done });
    if (timeoutMs != null) {
      setTimeout(done, timeoutMs);
    }
  });
}
